import React, { useState } from 'react';
import { Phone, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { appointmentService } from '../../services/appointmentService';
import { useLanguage } from '../../hooks/useLanguage';
import { SlotPicker } from './SlotPicker';
import { InspectionRecap } from './InspectionRecap';
import { InvoiceModal } from '../invoices/InvoiceModal';
import { inspectionService } from '../../services/inspectionService';
import { toast } from 'react-hot-toast';
import {
    Appointment, MAX_COUNTER, dayKey, endTimeOf, errorCode, formatDay, formatMoney,
    interventionLabel, minutesUntil, styleOf, todayKey, vehicleLabel
} from './appointmentUtils';

interface Props {
    appointment: Appointment;
    onClose: () => void;
    onChanged: () => void;
}

type Mode = 'view' | 'move' | 'cancel';

// Panneau latéral d'un rendez-vous : rappel de la demande (véhicule, IA, devis), client, historique
export const AppointmentDrawer: React.FC<Props> = ({ appointment: a, onClose, onChanged }) => {
    const { t, currentLanguage, isRTL } = useLanguage();
    const navigate = useNavigate();
    const [mode, setMode] = useState<Mode>('view');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [reason, setReason] = useState('');
    const [date, setDate] = useState(todayKey());
    const [timeSlot, setTimeSlot] = useState('');
    const [invoiceCtx, setInvoiceCtx] = useState<{ inspection: any; quote: any } | null>(null);

    const mins = minutesUntil(a);
    const soon = a.status === 'CONFIRMED' && mins != null && mins >= 0 && mins <= 120;
    const canMove = a.status === 'CONFIRMED' && (a.counterCount || 0) < MAX_COUNTER;
    const label = interventionLabel(a, t(`appointments.type.${a.type}`, { defaultValue: a.type }));

    const run = async (fn: () => Promise<any>, close = false) => {
        setBusy(true); setError('');
        try { await fn(); onChanged(); if (close) onClose(); }
        catch (err) { setError(t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') })); }
        finally { setBusy(false); }
    };
    const setStatus = (status: string, extra: any = {}, close = false) => run(() => appointmentService.updateStatus(a._id, { status, ...extra }), close);

    // « Terminer » suit la même règle que l'inspection : on passe par la facture, qui termine l'inspection,
    // et l'inspection terminée termine le rendez-vous (côté serveur). Sans inspection liée : statut direct.
    const complete = async () => {
        if (!a.inspectionId) return setStatus('COMPLETED', {}, true);
        setBusy(true); setError('');
        try {
            const insp: any = await inspectionService.getInspectionById(String(a.inspectionId));
            if (insp.status === 'completed') { await appointmentService.updateStatus(a._id, { status: 'COMPLETED' }); onChanged(); onClose(); return; }
            const quote = insp.quotes?.find((q: any) => q.status === 'accepted_with_payment' || q.status === 'accepted_no_payment');
            if (!quote) { setError(t('inspectionDetail.messages.noAcceptedQuote')); return; }
            if (insp.status === 'accepted') await inspectionService.updateInspectionStatus(insp._id, 'in_progress');
            setInvoiceCtx({ inspection: { ...insp, status: 'in_progress' }, quote });
        } catch (err) {
            setError(t(`appointments.errors.${errorCode(err)}`, { defaultValue: t('appointments.errors.generic') }));
        } finally { setBusy(false); }
    };

    const Row: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
        <div className="flex items-start justify-between gap-3 px-3 py-2.5 border-b border-gray-100 last:border-0 text-sm">
            <span className="text-gray-500">{title}</span><div className="text-end">{children}</div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />
            <aside className={`absolute inset-y-0 ${isRTL ? 'left-0' : 'right-0'} w-full sm:w-[440px] bg-white shadow-2xl flex flex-col`} dir={isRTL ? 'rtl' : 'ltr'}>
                <div className="px-5 pt-5 pb-4 border-b border-gray-200">
                    <div className="flex items-start justify-between gap-3">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${soon ? 'bg-indigo-600 text-white' : styleOf(a.status).badge}`}>
                            {soon ? t('appointments.drawer.nextIn', { minutes: mins }) : t(`appointments.status.${a.status}`)}
                        </span>
                        <button onClick={onClose} aria-label={t('appointments.actions.close')} className="p-1.5 rounded-full hover:bg-gray-100"><X size={18} /></button>
                    </div>
                    <h2 className="mt-2 text-xl font-extrabold text-gray-900">{a.timeSlot} – {endTimeOf(a)} · {a.clientName}</h2>
                    <p className="text-sm text-gray-600 mt-0.5">
                        {vehicleLabel(a)} · {label} · {a.serviceMode === 'WAIT' ? t('appointments.serviceMode.waitShort') : t('appointments.serviceMode.dropShort')}
                    </p>
                    <p className="text-xs text-gray-400 mt-1 first-letter:uppercase">{formatDay(dayKey(a.date), currentLanguage)}</p>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                    {a.awaiting === 'CLIENT' && <p className="text-sm text-amber-800 bg-amber-50 rounded-lg px-3 py-2">{t('appointments.detail.waitingClient')}</p>}

                    <div className="rounded-xl border border-gray-200 overflow-hidden">
                        <Row title={t('appointments.request.client')}>
                            <span className="font-semibold text-gray-900 me-2">{a.clientName}</span>
                            {a.clientPhone && <a href={`tel:${a.clientPhone}`} className="text-indigo-600 font-semibold me-2 inline-flex items-center gap-1"><Phone size={12} />{t('appointments.drawer.call')}</a>}
                            {a.inspectionId && <button onClick={() => navigate('/dashboard/messages')} className="text-indigo-600 font-semibold">{t('appointments.drawer.write')}</button>}
                        </Row>
                        <Row title={t('appointments.request.acceptedQuote')}><span className="font-bold text-green-700">{formatMoney(a.quoteSnapshot?.totalTTC, currentLanguage)}</span></Row>
                        {(a.clientNote || a.description) && <div className="px-3 py-2.5 text-sm"><p className="text-gray-500">{t('appointments.detail.note')}</p><p className="text-gray-800 mt-0.5">« {a.clientNote || a.description} »</p></div>}
                    </div>

                    <InspectionRecap appointment={a} />

                    {(a.history || []).length > 0 && (
                        <div>
                            <p className="text-sm font-bold text-gray-900 mb-2">{t('appointments.drawer.history')}</p>
                            <ul className="space-y-1.5">
                                {(a.history || []).map((h, i) => (
                                    <li key={i} className="text-sm text-gray-700">
                                        <span className="text-gray-400">{new Intl.DateTimeFormat(currentLanguage === 'ar' ? 'ar' : currentLanguage === 'en' ? 'en-GB' : 'fr-FR', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(h.at))}</span>
                                        {' · '}{t(`appointments.history.${h.event}`, { defaultValue: h.event })}{h.text && h.text !== 'auto' ? ` (${h.text})` : ''}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {mode === 'move' && (
                        <div className="rounded-xl border border-indigo-200 p-3 space-y-3">
                            <p className="font-bold text-gray-900 text-sm">{t('appointments.actions.counter')}</p>
                            <SlotPicker date={date} value={timeSlot} duration={a.duration} onDateChange={setDate} onChange={setTimeSlot} />
                        </div>
                    )}
                    {mode === 'cancel' && (
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700">{t('appointments.actions.reasonLabel')}</label>
                            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm focus:border-indigo-500 focus:ring-indigo-500" />
                        </div>
                    )}
                    {error && <p role="alert" className="text-sm text-red-700 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
                </div>

                <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-end gap-2">
                    {mode !== 'view' && <button onClick={() => { setMode('view'); setError(''); }} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">{t('appointments.actions.back')}</button>}
                    {mode === 'view' && (<>
                        {['REQUESTED', 'COUNTER_PROPOSED', 'CHANGE_REQUESTED', 'CONFIRMED'].includes(a.status) && (
                            <button onClick={() => setMode('cancel')} className="px-4 py-2 rounded-lg border border-red-300 text-red-700 text-sm font-semibold hover:bg-red-50">{t('appointments.actions.cancel')}</button>
                        )}
                        {canMove && <button onClick={() => setMode('move')} className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-800 text-sm font-semibold hover:bg-gray-50">{t('appointments.actions.move')}</button>}
                        {a.status === 'CONFIRMED' && <button disabled={busy} onClick={() => setStatus('NO_SHOW', {}, true)} className="px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100">{t('appointments.actions.noShow')}</button>}
                        {a.status === 'CONFIRMED' && <button disabled={busy} onClick={complete} className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60">{t('appointments.actions.complete')}</button>}
                    </>)}
                    {mode === 'move' && <button disabled={busy || !timeSlot} onClick={() => run(() => appointmentService.reschedule(a._id, { date, timeSlot }), true)} className="px-5 py-2 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">{t('appointments.actions.sendProposal')}</button>}
                    {mode === 'cancel' && <button disabled={busy} onClick={() => setStatus('CANCELLED', { cancelReason: reason }, true)} className="px-5 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-60">{t('appointments.actions.cancelAppointment')}</button>}
                </div>
            </aside>
            {invoiceCtx && (
                <InvoiceModal
                    inspection={invoiceCtx.inspection}
                    acceptedQuote={invoiceCtx.quote}
                    onClose={() => setInvoiceCtx(null)}
                    onInvoiceCreated={() => { setInvoiceCtx(null); toast.success(t('inspectionDetail.messages.invoiceCreated')); onChanged(); onClose(); }}
                />
            )}
        </div>
    );
};
